import React, { useState, useEffect } from "react";
import { Drawer, IconButton, List, ListItem, ListItemText } from "@material-ui/core";
import {
  Home as HomeIcon,
  NotificationsNone as NotificationsIcon,
  FormatSize as TypographyIcon,
  FilterNone as UIElementsIcon,
  BorderAll as TableIcon,
  QuestionAnswer as SupportIcon,
  LibraryBooks as LibraryIcon,
  HelpOutline as FAQIcon,
  ArrowBack as ArrowBackIcon,
} from "@material-ui/icons";
import { useTheme } from "@material-ui/styles";
import { withRouter } from "react-router-dom";
import classNames from "classnames";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import GavelIcon from "@mui/icons-material/Gavel"; // for agreements/contracts
// styles
import useStyles from "./styles";

// components
import SidebarLink from "./components/SidebarLink/SidebarLink";
import Dot from "./components/Dot";

// context
import {
  useLayoutState,
  useLayoutDispatch,
  toggleSidebar,
} from "../../context/LayoutContext";

function Sidebar({ location, user }) {
  var classes = useStyles();
  var theme = useTheme();

  // global
  var { isSidebarOpened } = useLayoutState();
  var layoutDispatch = useLayoutDispatch();

  // local
  var [isPermanent, setPermanent] = useState(true);

  useEffect(() => {
    window.addEventListener("resize", handleWindowWidthChange);
    handleWindowWidthChange();
    return () => {
      window.removeEventListener("resize", handleWindowWidthChange);
    };
  }, []);

  // Determine user role (user comes from Layout - single source of truth, no duplicate fetch)
  const userRole = user?.role || null;
  const isAdmin = user?.is_staff || user?.is_superuser || false;
  const isDemoUser = userRole === "DEMO";
  const isBulkImportUser = userRole === "BULK_IMPORT";

  // Build menu structure based on role
  let structure = [];
  
  if (isDemoUser) {
    // DEMO users only see Demo Link section
    structure = [
      {
        id: 101,
        label: "Demo Link",
        link: "/app/demo-access",
        icon: <i class="fa-solid fa-link" style={{ fontSize: "20px", color: "#0B194A" }}></i>,
      },
    ];
  } else if (isBulkImportUser) {
    // BULK_IMPORT users only see Bulk Import section
    structure = [
      { 
        id: 0, 
        label: "Bulk Import", 
        link: "/app/upload", 
        icon: <i class="fa-solid fa-upload" style={{ fontSize: "22px", color: "blue" }}></i> 
      }
    ];
  } else if (isAdmin) {
    // ADMIN users see all sections
    structure = [
    { id: 0, label: "Bulk Import", link: "/app/upload", icon: <i class="fa-solid fa-upload" style={{ fontSize: "22px", color: "blue" }}></i> },
    { id: 1, label: "Manage Town", link: "/app/muncipality", icon: <HomeIcon style={{ fontSize: "30px", color: "blue" }} /> },
    {
      id: 101,
      label: "Demo Link",
      link: "/app/demo-access",
      icon: <i class="fa-solid fa-link" style={{ fontSize: "20px", color: "#0B194A" }}></i>,
    },
    {
      id: 17,
      label: "Manage Aware Capture",
      link: "/app/aware-capture",
      icon: <i class="fa-solid fa-calendar-check" style={{ fontSize: "20px", color: "#0B194A" }}></i>

    },
    { id: 2, label: "Manage Youtube Channel", link: "/app/manage", icon: <i class="fa-brands fa-youtube" style={{ fontSize: "22px", color: "red" }}></i> },
    { id: 3, label: "YouTube Transcript Summary", link: "/app/summary", icon: <i class="fa-solid fa-book" style={{ fontSize: "22px", color: "green" }}></i> },
    { id: 13, label: "Scrape & Transcript Logs", link: "/app/transcript-logs", icon: <i class="fa-solid fa-server" style={{ fontSize: "20px", color: "#1976d2" }}></i> },
    { id: 4, label: "Manage Dictionary", link: "/app/dictionary", icon: <i class="fa-solid fa-spell-check" style={{ fontSize: "22px", color: "green" }}></i> },

    { id: 5, label: "Manage Users", link: "/app/users", icon: <i class="fa fa-users" style={{ fontSize: "20px", color: "#0B194A" }}></i> },
    { id: 6, label: "Manage Notifications", link: "/app/notifications", icon: <i class="fa fa-bell" style={{ fontSize: "20px", color: "#0B194A" }}></i> },
    {
      id: 7,
      label: "AI Powered Chat",
      link: "/app/chat",
      icon: <i class="fa fa-comments" style={{ fontSize: "20px", color: "#0B194A" }}></i>
    },
    { id: 8, label: "BoE Mappings", link: "/app/mapping", icon: <i class="fa-brands fa-connectdevelop" style={{ fontSize: "20px", color: "#0B194A" }}></i> },
    { id: 9, label: "Manage Indexing", link: "/app/reindex", icon: <i class="fa-solid fa-database" style={{ fontSize: "20px", color: "#0B194A" }}></i> },
    { id: 18, label: "Summary Status", link: "/app/video-summary-recovery", icon: <i class="fa-solid fa-list-check" style={{ fontSize: "20px", color: "#0B194A" }}></i> },
    { id: 10, label: "Stats Page", link: "/app/stats", icon: <i class="fa-solid fa-chart-simple" style={{ fontSize: "20px", color: "#0B194A" }}></i> },
    { id: 11, label: "Manage API", link: "/app/manage-api", icon: <i class="fa-solid fa-gear" style={{ fontSize: "20px", color: "#0B194A" }}></i> },
    {
      id: 12,
      label: "Reported Issues",
      link: "/app/reported-issues",
      icon: <i class="fa-solid fa-clipboard-list" style={{ fontSize: "20px", color: "#0B194A" }}></i>
    },
    {
      id: 13,
      label: "Feedback",
      link: "/app/feedbacks",
      icon: <i class="fa-solid fa-comments" style={{ fontSize: "20px", color: "#0B194A" }}></i>
    },

    {
      id: 14,
      label: "Manage Plans",
      link: "/app/plans",
      icon: <ManageAccountsIcon style={{ fontSize: 20, color: "#0B194A" }} />,
    },
    {
      id: 15,
      label: "City Agreement",
      link: "/app/city-agreement",
      icon: <GavelIcon style={{ fontSize: 20, color: "#0B194A" }} />,
    },
    ];
  } else {
    // Default: no access (should not happen, but handle gracefully)
    structure = [];
  }

  const showLoading = user === undefined;

  return (
    <Drawer
      variant={isPermanent ? "permanent" : "temporary"}
      className={classNames(classes.drawer, {
        [classes.drawerOpen]: isSidebarOpened,
        [classes.drawerClose]: !isSidebarOpened,
      })}
      classes={{
        paper: classNames({
          [classes.drawerOpen]: isSidebarOpened,
          [classes.drawerClose]: !isSidebarOpened,
        }),
      }}
      open={isSidebarOpened}
    >
      <div className={classes.toolbar} />
      <div className={classes.mobileBackButton}>
        <IconButton onClick={() => toggleSidebar(layoutDispatch)}>
          <ArrowBackIcon
            classes={{
              root: classNames(classes.headerIcon, classes.headerIconCollapse),
            }}
          />
        </IconButton>
      </div>
      <List className={classes.sidebarList}>
        {showLoading ? (
          <ListItem>
            <ListItemText primary="Loading…" />
          </ListItem>
        ) : (
          structure.map(link => (
            <SidebarLink
              key={link.id}
              location={location}
              isSidebarOpened={isSidebarOpened}
              {...link}
            />
          ))
        )}
      </List>
    </Drawer>
  );

  // ##################################################################
  function handleWindowWidthChange() {
    var windowWidth = window.innerWidth;
    var breakpointWidth = theme.breakpoints.values.md;
    var isSmallScreen = windowWidth < breakpointWidth;

    if (isSmallScreen && isPermanent) {
      setPermanent(false);
    } else if (!isSmallScreen && !isPermanent) {
      setPermanent(true);
    }
  }
}

export default withRouter(Sidebar);
